/* ==========================================================================
   DIREVULF — pdp-viewer.js

   The fullscreen product image viewer. It decides WHICH picture is showing
   and WHEN the viewer is on screen; it does not reimplement a modal. The
   theme already has one — DirevulfOverlay in theme.js — which owns the focus
   trap, Escape, the scroll lock and returning focus to whatever opened it.
   One overlay implementation in this theme, not two that drift apart.

   What this file adds on top of that:

     - opening from any gallery image, on the picture that was clicked
     - arrows, arrow keys and a horizontal swipe, all changing nothing but
       the picture
     - zoom and pan: wheel, pinch, double-click/double-tap to magnify, drag
       to move around, all written to one CSS transform on the picture and
       nothing else on the page
     - a history entry, so the browser's Back button closes the viewer
       instead of leaving the product page
     - the shopper's exact scroll position, restored on close

   THE CONTROLS ARE NEVER PART OF ANY OF IT. The close control and the two
   arrows are siblings of the stage, outside it, and the stage clips what it
   holds — so a magnified picture is bounded by the stage, cannot paint over
   a control, and a drag can only ever begin on the picture. No zoom level
   moves, scales or hides a control.

   THE VIEWER IS MOVED TO <body>. A `position: fixed` element is positioned
   against the viewport only while no ancestor has a transform, a filter or
   containment; inside one it is positioned against that ancestor instead and
   a "fullscreen" overlay quietly becomes a box in the middle of the page.
   The product section is not such an ancestor today, and this is here so it
   never matters if one is introduced.

   NOTHING HERE TOUCHES THE PRODUCT PAGE. No variant is read or written, no
   form is submitted, no URL is rewritten — the pushed history entry carries
   the page's own address, so the selected variant survives untouched and
   closing does not reload anything.
   ========================================================================== */
(function () {
  'use strict';

  function $(sel, root) { return (root || document).querySelector(sel); }
  function $$(sel, root) { return Array.prototype.slice.call((root || document).querySelectorAll(sel)); }

  var viewer = null;
  var slides = [];
  var live = null;
  var dotsBox = null;
  var dots = [];
  var index = 0;
  var scrollY = 0;
  var pushed = false;
  var open = false;
  var downX = null;
  var downY = null;
  var dragged = false;
  var settling = false;

  /* ------------------------------------------------------------ zoom state
     One picture is magnified at a time, so one set of numbers describes it:
     `scale` and a translation in screen pixels, both written to the active
     <img> as a single CSS transform. `fit` is the picture as it is drawn at
     scale 1 — the `object-fit: contain` result — and is what the pan limits
     are measured against, not the element box, because the box includes the
     letterboxing either side of the picture. */
  var MAX_SCALE = 4;        /* ~1 source pixel per drawn pixel at 2048w on a desktop stage */
  var TAP_SCALE = 2.5;      /* where a double-click / double-tap lands */
  var scale = 1;
  var tx = 0;
  var ty = 0;
  var fit = { w: 0, h: 0, boxW: 0, boxH: 0 };
  var easeTimer = null;
  var pointers = {};
  var pointerCount = 0;
  var single = null;
  var pinch = null;
  var lastTapAt = 0;
  var lastTapX = 0;
  var lastTapY = 0;

  /* ------------------------------------------------------------- pictures */

  /* No slide carries a src until the viewer needs it, so a shopper who never
     opens the viewer downloads nothing beyond the product page's own images. */
  function load(i) {
    var slide = slides[i];
    if (!slide) return null;
    var img = $('img', slide);
    if (!img) return null;
    if (!img.getAttribute('src')) {
      var srcset = img.getAttribute('data-dv-pv-srcset');
      if (srcset) img.setAttribute('srcset', srcset);
      img.setAttribute('src', img.getAttribute('data-dv-pv-src') || '');
    }
    return img;
  }

  /* The one either side as well, so an arrow press has the picture already. */
  function loadAround(i) {
    load(i);
    if (slides.length > 1) {
      load((i + 1) % slides.length);
      load((i - 1 + slides.length) % slides.length);
    }
  }

  function show(next) {
    if (!slides.length) return;
    /* Wrap, so neither arrow ever dead-ends. */
    index = (next + slides.length) % slides.length;
    /* A new picture always arrives at fit, and the one being left behind is
       put back to fit too. */
    clearZoom();
    load(index);
    for (var i = 0; i < slides.length; i++) {
      var on = i === index;
      slides[i].classList.toggle('is-on', on);
      if (on) slides[i].removeAttribute('aria-hidden');
      else slides[i].setAttribute('aria-hidden', 'true');
    }
    if (live) live.textContent = 'Image ' + (index + 1) + ' of ' + slides.length;
    for (var d = 0; d < dots.length; d++) {
      var act = d === index;
      dots[d].classList.toggle('is-active', act);
      if (act) dots[d].setAttribute('aria-current', 'true');
      else dots[d].removeAttribute('aria-current');
    }
    placeDots();
  }

  /* The dots sit just under the picture as it is drawn at fit, not under the
     stage: a landscape picture is letterboxed top and bottom, and dots at the
     foot of the stage would float far below it. The fitted height is worked
     out from the file's own dimensions — the loaded file's if it has
     arrived, otherwise the width/height the snippet printed from Shopify —
     so the row lands in its final place before the picture has loaded and
     never jumps. Zoom does not move it. */
  var DOT_GAP = 12;
  function placeDots() {
    if (!dotsBox || !open) return;
    var slide = activeSlide();
    var img = activeImg();
    if (!slide || !img) return;
    var panel = dotsBox.offsetParent || viewer;
    var r = slide.getBoundingClientRect();
    var pr = panel.getBoundingClientRect();
    if (!r.height) return;
    var nw = img.naturalWidth || parseFloat(img.getAttribute('width')) || 0;
    var nh = img.naturalHeight || parseFloat(img.getAttribute('height')) || 0;
    var drawnH = r.height;
    if (nw && nh) drawnH = Math.min(r.height, nh * Math.min(r.width / nw, r.height / nh));
    var top = r.top - pr.top + (r.height + drawnH) / 2 + DOT_GAP;
    var maxTop = pr.height - dotsBox.offsetHeight - 8;
    dotsBox.style.top = Math.round(Math.max(0, Math.min(top, maxTop))) + 'px';
  }

  /* ------------------------------------------------------------------ zoom */

  function activeSlide() { return slides[index] || null; }
  function activeImg() { var el = activeSlide(); return el ? el.querySelector('img') : null; }

  /* The drawn picture at scale 1. offsetWidth/offsetHeight are the LAYOUT box
     and are not affected by the transform — getBoundingClientRect() is, and
     using it here would feed the zoom its own output. */
  function measure() {
    var img = activeImg();
    if (!img) return false;
    var boxW = img.offsetWidth, boxH = img.offsetHeight;
    var nw = img.naturalWidth, nh = img.naturalHeight;
    if (!boxW || !boxH || !nw || !nh) return false;
    var f = Math.min(boxW / nw, boxH / nh);
    fit = { w: nw * f, h: nh * f, boxW: boxW, boxH: boxH };
    return true;
  }

  /* The picture may be moved only as far as its own edges: once an edge would
     come inside the stage, it stops. Below the point where the picture is
     bigger than the stage on an axis there is nothing to pan, so it is held
     dead centre — which is also what keeps an unzoomed picture centred. */
  function clampPan() {
    var maxX = Math.max(0, (fit.w * scale - fit.boxW) / 2);
    var maxY = Math.max(0, (fit.h * scale - fit.boxH) / 2);
    if (tx > maxX) tx = maxX; else if (tx < -maxX) tx = -maxX;
    if (ty > maxY) ty = maxY; else if (ty < -maxY) ty = -maxY;
  }

  function paint(ease) {
    var img = activeImg();
    if (!img) return;
    window.clearTimeout(easeTimer);
    if (ease) {
      img.classList.add('is-easing');
      easeTimer = window.setTimeout(function () { img.classList.remove('is-easing'); }, 240);
    } else {
      img.classList.remove('is-easing');
    }
    img.style.transform = 'translate(' + tx.toFixed(2) + 'px, ' + ty.toFixed(2) + 'px) scale(' + scale.toFixed(4) + ')';
    var slide = activeSlide();
    if (slide) slide.classList.toggle('is-zoomed', scale > 1.001);
  }

  /* The centre of the stage in viewport coordinates. Read from the SLIDE,
     which is never transformed, so it stays true at any zoom level. */
  function stageCentre() {
    var slide = activeSlide();
    if (!slide) return null;
    var r = slide.getBoundingClientRect();
    return { x: r.left + r.width / 2, y: r.top + r.height / 2 };
  }

  /* Zoom to `next`, keeping whatever is under (ax, ay) exactly where it is —
     so the wheel magnifies what the cursor is over and a pinch magnifies what
     is between the fingers, rather than always the middle of the picture. */
  function zoomTo(next, ax, ay, ease) {
    if (!measure()) return;
    next = Math.min(MAX_SCALE, Math.max(1, next));
    var c = stageCentre();
    if (!c) return;
    var px = (typeof ax === 'number') ? ax - c.x : 0;
    var py = (typeof ay === 'number') ? ay - c.y : 0;
    var k = next / scale;
    tx = px - (px - tx) * k;
    ty = py - (py - ty) * k;
    scale = next;
    clampPan();
    paint(ease);
  }

  function resetZoom(ease) {
    scale = 1; tx = 0; ty = 0;
    paint(ease);
  }

  /* Every slide back to fit, including the one being left behind, so an image
     never reappears still magnified from last time. */
  function clearZoom() {
    scale = 1; tx = 0; ty = 0;
    window.clearTimeout(easeTimer);
    for (var i = 0; i < slides.length; i++) {
      var img = slides[i].querySelector('img');
      if (img) { img.classList.remove('is-easing'); img.style.transform = 'translate(0px, 0px) scale(1)'; }
      slides[i].classList.remove('is-zoomed', 'is-panning');
    }
  }

  function zoomed() { return scale > 1.001; }

  function tapped(x, y) {
    var now = Date.now();
    if (now - lastTapAt < 320 && Math.abs(x - lastTapX) < 36 && Math.abs(y - lastTapY) < 36) {
      lastTapAt = 0;
      if (zoomed()) resetZoom(true);
      else zoomTo(TAP_SCALE, x, y, true);
      return;
    }
    lastTapAt = now; lastTapX = x; lastTapY = y;
  }

  /* --------------------------------------------------------- open / close */

  function openViewer(i, trigger) {
    if (!viewer || open) return;
    scrollY = window.pageYOffset || document.documentElement.scrollTop || 0;
    show(i);
    loadAround(index);
    open = true;
    pointers = {}; pointerCount = 0; single = null; pinch = null;

    /* One entry for the viewer, whatever happens inside it — the arrows do
       not deep-link, so they do not push. The address is the page's own, so
       Back returns to this page, in place, with its variant intact.
       Pushed BEFORE the scroll is locked: the browser keeps the scroll
       position of the entry being left as it is at this moment, and locking
       the scroll can reflow the page by a few pixels (a desktop scrollbar
       disappearing), which Back would otherwise restore. */
    try {
      window.history.pushState({ dvPhotoViewer: true }, '', window.location.href);
      pushed = true;
    } catch (e) {
      pushed = false;
    }

    if (window.DirevulfOverlay && window.DirevulfOverlay.open) {
      window.DirevulfOverlay.open(viewer, trigger);
    } else {
      /* theme.js absent or failed: the viewer still opens and still closes. */
      viewer.hidden = false;
      document.body.style.overflow = 'hidden';
      var panel = $('.dv-pv__panel', viewer);
      if (panel) panel.focus();
    }
    placeDots();
  }

  /* Everything closes through the overlay controller, and the tidying up
     happens on the event it fires — so Escape, the X, a click on the
     controller's own scrim and the Back button all take the same path. */
  function closeViewer() {
    if (!open) return;
    if (window.DirevulfOverlay && window.DirevulfOverlay.active === viewer) {
      window.DirevulfOverlay.close();
      return;
    }
    viewer.hidden = true;
    document.body.style.overflow = '';
    finish();
  }

  /* Runs once per close, whichever route got here. */
  function finish() {
    if (!open) return;
    open = false;
    /* Closed at fit, so reopening never starts part-way into a zoom. */
    clearZoom();
    pointers = {}; pointerCount = 0; single = null; pinch = null;

    /* Restore synchronously: DirevulfOverlay has just returned focus to the
       gallery image, and focusing an element the browser considers off screen
       scrolls the page to it. Putting the position back in the same turn of
       the event loop means that never reaches a paint. */
    restoreScroll();
    window.requestAnimationFrame(restoreScroll);

    if (pushed) {
      pushed = false;
      /* Drop the viewer's own entry so Back does not have to be pressed
         twice. Same address, so this is not a navigation and nothing
         reloads. */
      settling = true;
      try { window.history.back(); } catch (e) { settling = false; /* nothing to go back to */ }
    }
  }

  function restoreScroll() {
    var now = window.pageYOffset || document.documentElement.scrollTop || 0;
    if (Math.abs(now - scrollY) > 1) window.scrollTo(0, scrollY);
  }

  /* ------------------------------------------------------------- wiring */

  function init() {
    viewer = $('[data-dv-photo-viewer]');
    if (!viewer) return;

    /* See the header comment. Done once, before anything can open it. */
    if (viewer.parentNode !== document.body) document.body.appendChild(viewer);

    slides = $$('[data-dv-pv-slide]', viewer);
    live = $('[data-dv-pv-live]', viewer);
    dotsBox = $('[data-dv-pv-dots]', viewer);
    dots = dotsBox ? $$('[data-dv-pv-dot]', dotsBox) : [];
    if (!slides.length) return;
    /* A slide's file arriving can only confirm the fitted size the snippet's
       dimensions predicted, but re-place anyway in case Shopify's numbers
       and the file disagree. */
    slides.forEach(function (slide) {
      var im = slide.querySelector('img');
      if (im) im.addEventListener('load', function () { if (open && slide === activeSlide()) placeDots(); });
    });

    document.addEventListener('click', function (event) {
      var opener = event.target.closest('[data-dv-photo-open]');
      if (opener) {
        event.preventDefault();
        /* On a phone the gallery is a horizontal snap row and the image IS
           the button, so a swipe along the row can end as a click on the
           image it started on. A gesture that travelled is a scroll, not a
           press, and must leave the gallery alone. */
        if (dragged) { dragged = false; return; }
        openViewer(parseInt(opener.getAttribute('data-dv-photo-open'), 10) || 0, opener);
        return;
      }
      if (!open) return;
      if (event.target.closest('[data-dv-pv-prev]')) { event.preventDefault(); show(index - 1); return; }
      if (event.target.closest('[data-dv-pv-next]')) { event.preventDefault(); show(index + 1); return; }
      var dot = event.target.closest('[data-dv-pv-dot]');
      if (dot) {
        event.preventDefault();
        var to = parseInt(dot.getAttribute('data-dv-pv-dot'), 10) || 0;
        if (to !== index) { show(to); loadAround(index); }
      }
    });

    /* Fetch the picture the moment a press begins rather than when it ends:
       by the time the click lands, the decode is already under way. And note
       where the press began, so a swipe along the gallery can be told from a
       press on it. */
    document.addEventListener('pointerdown', function (event) {
      var opener = event.target.closest('[data-dv-photo-open]');
      if (!opener) return;
      dragged = false;
      downX = event.clientX;
      downY = event.clientY;
      load(parseInt(opener.getAttribute('data-dv-photo-open'), 10) || 0);
    }, { passive: true });

    document.addEventListener('pointermove', function (event) {
      if (dragged || downX === null) return;
      if (Math.abs(event.clientX - downX) > 10 || Math.abs(event.clientY - downY) > 10) dragged = true;
    }, { passive: true });

    document.addEventListener('pointerup', function () { downX = null; downY = null; }, { passive: true });
    document.addEventListener('pointercancel', function () { downX = null; downY = null; dragged = false; }, { passive: true });

    /* PRESSING A GALLERY IMAGE MUST NOT MOVE THE PAGE UNDER IT.

       The browser moves focus on mousedown, and moving focus to an element it
       considers off screen scrolls the page until that element is fully in
       view. A gallery image is around 930px tall on a desktop column, so one
       is almost always partly out of view — measured in this theme's own
       harness, pressing the second image moved the page up 120px before the
       viewer had even opened. The viewer then covers the screen, so nobody
       sees it happen; what they see is the product page in the wrong place
       when they close it again.

       preventDefault() on mousedown suppresses the browser's focusing, not
       the click, so the viewer still opens; the focus is then taken by hand
       without the scroll, which is what lets DirevulfOverlay hand focus back
       to this exact image on close. Same fix, same reason, as the carousel
       arrows and the search microphone. */
    document.addEventListener('mousedown', function (event) {
      var opener = event.target.closest('[data-dv-photo-open]');
      if (!opener) return;
      event.preventDefault();
      try { opener.focus({ preventScroll: true }); } catch (e) { opener.focus(); }
    });

    /* Arrow keys, but only while the viewer is the thing on screen. Escape is
       the overlay controller's, and is deliberately not duplicated here. */
    document.addEventListener('keydown', function (event) {
      if (!open || slides.length < 2) return;
      if (event.key === 'ArrowLeft') { event.preventDefault(); show(index - 1); }
      else if (event.key === 'ArrowRight') { event.preventDefault(); show(index + 1); }
    });

    /* ------------------------------------------------------------ gestures

       One engine, pointer events, for mouse and finger alike, because the
       gestures overlap: at fit a horizontal drag is a swipe to the next
       picture, and magnified the same drag pans the picture instead. Two
       fingers always pinch. A press that does not travel is a tap, and two
       of those in quick succession zoom.

       The stage is `touch-action: none`, so none of this competes with the
       browser's own panning or pinch-to-zoom — which is what the brief means
       by not using the browser's zoom. */
    var stage = $('[data-dv-pv-stage]', viewer);
    if (stage) {
      stage.addEventListener('wheel', function (event) {
        if (!open) return;
        /* Without this the page (or the browser's own zoom, on ctrl+wheel)
           takes the gesture instead of the picture. */
        event.preventDefault();
        if (!measure()) return;
        /* Exponential, so a notch feels the same at every zoom level, and
           deltaMode 1 (a mouse reporting lines rather than pixels) is scaled
           to match. */
        var step = event.deltaMode === 1 ? event.deltaY * 16 : event.deltaY;
        zoomTo(scale * Math.exp(-step * 0.0018), event.clientX, event.clientY, false);
      }, { passive: false });

      stage.addEventListener('pointerdown', function (event) {
        if (!open) return;
        pointers[event.pointerId] = { x: event.clientX, y: event.clientY };
        pointerCount++;
        try { stage.setPointerCapture(event.pointerId); } catch (e) { /* not fatal */ }

        if (pointerCount === 1) {
          single = { id: event.pointerId, x: event.clientX, y: event.clientY,
                     tx: tx, ty: ty, at: Date.now(), moved: false };
          pinch = null;
        } else if (pointerCount === 2) {
          var ids = Object.keys(pointers);
          var a = pointers[ids[0]], b = pointers[ids[1]];
          var c = stageCentre();
          if (c && measure()) {
            pinch = {
              dist: Math.max(1, Math.hypot(b.x - a.x, b.y - a.y)),
              midX: (a.x + b.x) / 2 - c.x,
              midY: (a.y + b.y) / 2 - c.y,
              scale: scale, tx: tx, ty: ty
            };
          }
          single = null;
        }
      });

      stage.addEventListener('pointermove', function (event) {
        if (!open || !pointers[event.pointerId]) return;
        pointers[event.pointerId].x = event.clientX;
        pointers[event.pointerId].y = event.clientY;

        if (pointerCount >= 2 && pinch) {
          var ids = Object.keys(pointers);
          var a = pointers[ids[0]], b = pointers[ids[1]];
          if (!a || !b) return;
          var c = stageCentre();
          if (!c) return;
          event.preventDefault();
          var dist = Math.max(1, Math.hypot(b.x - a.x, b.y - a.y));
          var next = Math.min(MAX_SCALE, Math.max(1, pinch.scale * (dist / pinch.dist)));
          var midX = (a.x + b.x) / 2 - c.x;
          var midY = (a.y + b.y) / 2 - c.y;
          var k = next / pinch.scale;
          /* The point that was between the fingers when the pinch began stays
             between them — so the fingers also carry the picture with them. */
          tx = midX - (pinch.midX - pinch.tx) * k;
          ty = midY - (pinch.midY - pinch.ty) * k;
          scale = next;
          clampPan();
          paint(false);
          return;
        }

        if (pointerCount === 1 && single && single.id === event.pointerId) {
          var dx = event.clientX - single.x;
          var dy = event.clientY - single.y;
          if (!single.moved && Math.hypot(dx, dy) > 6) single.moved = true;
          if (zoomed()) {
            event.preventDefault();
            var slide = activeSlide();
            if (slide) slide.classList.add('is-panning');
            tx = single.tx + dx;
            ty = single.ty + dy;
            clampPan();
            paint(false);
          }
        }
      }, { passive: false });

      function endPointer(event) {
        if (!pointers[event.pointerId]) return;
        var wasSingle = single && single.id === event.pointerId ? single : null;
        delete pointers[event.pointerId];
        pointerCount = Math.max(0, pointerCount - 1);
        try { stage.releasePointerCapture(event.pointerId); } catch (e) { /* already gone */ }

        var slide = activeSlide();
        if (slide) slide.classList.remove('is-panning');

        if (pinch && pointerCount < 2) {
          pinch = null;
          /* A pinch that ended below fit settles back to fit rather than
             leaving the picture stranded mid-gesture. */
          if (scale <= 1.001) resetZoom(true);
          /* A finger still down becomes a fresh pan rather than jumping by
             everything the pinch moved. */
          var left = Object.keys(pointers)[0];
          single = left ? { id: Number(left), x: pointers[left].x, y: pointers[left].y,
                            tx: tx, ty: ty, at: Date.now(), moved: true } : null;
          return;
        }

        if (pointerCount > 0 || !wasSingle) return;

        if (!wasSingle.moved) {
          tapped(event.clientX, event.clientY);
          single = null;
          return;
        }

        /* Travelled, at fit, and quick enough to be a flick: change picture.
           Magnified, the same gesture was a pan and is already done. */
        if (!zoomed() && slides.length > 1) {
          var dx2 = event.clientX - wasSingle.x;
          var dy2 = event.clientY - wasSingle.y;
          if (Math.abs(dx2) >= 40 && Math.abs(dx2) > Math.abs(dy2) && Date.now() - wasSingle.at <= 800) {
            show(dx2 < 0 ? index + 1 : index - 1);
          }
        }
        single = null;
      }

      stage.addEventListener('pointerup', endPointer);
      stage.addEventListener('pointercancel', function (event) {
        endPointer(event);
        single = null; pinch = null;
      });

      /* The stage changes size when the phone rotates or the address bar
         slides away. Keep the zoom, but re-measure so the pan limits match
         the new room — otherwise the picture can sit outside them. */
      window.addEventListener('resize', function () {
        if (!open) return;
        placeDots();
        if (!measure()) return;
        clampPan();
        paint(false);
      });
    }

    /* The single close path. The controller fires this for Escape, for the X
       (which carries data-dv-close) and for its own close() — so there is one
       place that restores the scroll position and one that tidies history. */
    document.addEventListener('dv:overlay:close', function (event) {
      if (!event.detail || event.detail.el !== viewer) return;
      finish();
    });

    /* Back closes the viewer instead of leaving the page. `pushed` is dropped
       first so finish() does not try to go back a second time. */
    window.addEventListener('popstate', function () {
      /* The browser restores the scroll position it saved for the entry it
         is returning to — saved when the viewer opened, which can differ by
         a few pixels from where the shopper was if the page reflowed as the
         scroll was locked (a desktop scrollbar disappearing). The viewer's
         own record wins. */
      if (!open) {
        if (settling) { settling = false; restoreScroll(); }
        return;
      }
      pushed = false;
      closeViewer();
    });
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init);
  else init();
})();
